/*
  scroll-story.js

  スクロール量を「映像の再生位置」に変換する（scroll scrubbing）。
  実際に動画を再生しているのではなく、連番画像をスクロール進捗に応じて
  canvas に差し替えて描いているだけ。スクロールを戻せば映像も巻き戻る。

  sections/scroll-story.liquid から <scroll-story> として使う。
*/
(() => {
  if (customElements.get('scroll-story')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* edge0→edge1 の区間を 0→1 に滑らかに写す（フェードのイージング用） */
  const smoothstep = (edge0, edge1, x) => {
    if (edge0 === edge1) return x < edge0 ? 0 : 1;
    const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  };

  /*
    Horizonのスクロールコンテナ(.page-wrapper)には assets/base.css で
    `scroll-behavior: smooth` が指定されている(990px以上)。
    CSSOM仕様上 `element.scrollTop = x` の既定 behavior は 'auto' で、
    'auto' は要素の scroll-behavior を採用する。つまり scrollTop への代入は
    そのままではスムーススクロール(数百ms のアニメーション)になる。
    レイアウトシフトの打ち消しのような「今すぐ効いてほしい補正」では、
    アニメーションしている間ずれたままの画面が見えてしまうため、
    必ず behavior:'instant' を明示して即座に反映させる。
    (テーマ標準の assets/scroll-container.js でも同じ理由で instant を使っている)
  */
  const setScrollTopInstant = (container, value) => {
    if (!container) return;
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: value, left: container.scrollLeft, behavior: 'instant' });
      /* behavior:'instant' 未対応のブラウザ向けフォールバック */
      if (Math.abs(container.scrollTop - value) > 1) {
        const previous = container.style.scrollBehavior;
        container.style.scrollBehavior = 'auto';
        container.scrollTop = value;
        container.style.scrollBehavior = previous;
      }
      return;
    }
    const previous = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';
    container.scrollTop = value;
    container.style.scrollBehavior = previous;
  };

  const CAPTION_FADE = 0.15; // キャプションの出入りに使う進捗幅
  const LOAD_CONCURRENCY = 6; // 画像の同時ダウンロード数
  const SETTLE_EPSILON = 0.0002; // これ未満の差になったら追従を打ち切って吸着する
  const DEFAULT_SMOOTHING = 0.12;
  const BOOTSTRAP_FRAME_COUNT = 30; // seqAがこの枚数読めたらseqBの読み込みを開始する
  const BOOTSTRAP_TIMEOUT_MS = 2500; // 読み込みが遅くても、この時間が経てばseqBを開始する

  /* ステップモード(data-scroll-mode="step")関連 */
  const DEFAULT_STEP_STOPS = [0, 0.55, 1];
  const WHEEL_GESTURE_WINDOW = 150; // 直前のwheelイベントからこの時間(ms)以内は同じ操作とみなす
  const TOUCH_STEP_THRESHOLD = 40; // 縦方向の移動量がこれを超えたら1ステップとみなす(px)
  const DEFAULT_STEP_DURATION = 1200; // ステップ遷移1回分の所要時間(ms)
  class ScrollStory extends HTMLElement {
    connectedCallback() {
      document.documentElement.classList.remove('scroll-story-complete');
      // モバイルでは既存スライダーを使うため、重い連番フレームは読み込まない。
      if (window.matchMedia('(max-width: 749px)').matches) {
        this.hidden = true;
        document.documentElement.classList.remove('scroll-story-immersive');
        return;
      }

      this.track = this.querySelector('[data-scroll-story-track]');
      this.canvas = this.querySelector('[data-scroll-story-canvas]');
      this.status = this.querySelector('[data-scroll-story-status]');
      this.endMarker = this.nextElementSibling?.matches('[data-scroll-story-end]') ? this.nextElementSibling : null;
      if (!this.track || !this.canvas) return;

      this.captions = Array.from(this.querySelectorAll('[data-scroll-story-caption]')).map((el) => ({
        el,
        start: Number(el.dataset.start || 0) / 100,
        end: Number(el.dataset.end || 100) / 100,
        fade: Number(el.dataset.fade || CAPTION_FADE * 100) / 100,
        travel: Number(el.dataset.travel || 0),
        offset: Number(el.dataset.offset || 0),
        offsetX: Number(el.dataset.offsetX || 0),
        exitZoom: Number(el.dataset.exitZoom || 100) / 100,
      }));

      this.ctx = this.canvas.getContext('2d', { alpha: false });
      /* seqA: 通常のシーケンス。seqB: 鏡面反射から浮かび上がる第2シーケンス(任意) */
      this.seqA = this.createSequence({
        frameCount: Number(this.dataset.frameCount) || 0,
        frameStart: Number(this.dataset.frameStart) || 1,
        framePad: Number(this.dataset.framePad) || 0,
        frameTemplate: this.dataset.frameTemplate || '',
      });
      this.seqB = this.createSequence({
        frameCount: Number(this.dataset.frameCountB) || 0,
        frameStart: Number(this.dataset.frameStartB) || 1,
        framePad: Number(this.dataset.framePadB) || 0,
        frameTemplate: this.dataset.frameTemplateB || '',
      });
      this.blendStart = this.parseBlendValue(this.dataset.blendStart, 0.6);
      this.blendEnd = this.parseBlendValue(this.dataset.blendEnd, 0.85);
      this.blendMode = this.parseBlendMode(this.dataset.blendMode);
      const maskRange = this.parseMaskRange(this.dataset.maskLow, this.dataset.maskHigh);
      this.maskLow = maskRange.maskLow;
      this.maskHigh = maskRange.maskHigh;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

      /*
        ステップモード: reduced-motion時は無効化して従来の連続モードで動かす。
        stepIndex は現在いるステップ番号。tick() では targetProgress を
        stops[stepIndex] に固定し、getBoundingClientRect からの算出をやめる。
      */
      this.stepMode = this.dataset.scrollMode === 'step' && !this.reduced;
      this.stops = this.parseStepStops();
      this.editorState = this.getEditorState();
      this.stepIndex = this.restoreEditorStepIndex();

      /*
        ステップ遷移(時間ベースのイージング)用の状態。
        stepAnimating が true の間、tick() は慣性補間を使わず
        stepFrom→stepTo を stepDuration かけて ease-in-out cubic で結ぶ。
      */
      this.stepDuration = this.parseStepDuration();
      this.stepAnimating = false;
      this.stepFrom = this.stops[this.stepIndex];
      this.stepTo = this.stops[this.stepIndex];
      this.stepStart = 0;

      /*
        mask ブレンド用のオフスクリーンcanvas。DOMには追加しない。
        maskCanvas: 宝石フレームの輝度マスク(低解像度でよい)。
        modelCanvas: モデルをマスク形状に切り抜くための作業用(本canvasと同サイズ)。
      */
      this.maskCanvas = document.createElement('canvas');
      this.maskCtx = this.maskCanvas.getContext('2d');
      this.modelCanvas = document.createElement('canvas');
      this.modelCtx = this.modelCanvas.getContext('2d');
      this.maskCacheIndex = -1; // このAフレーム番号でマスクを作った、というキャッシュ

      this.lastKey = null;
      this.reflectionCycleStart = 0;
      this.rafId = 0;
      this.isVisible = false;

      /*
        targetProgress: スクロール位置から算出した「あるべき」進捗。
        renderProgress: 実際に描画に使う、慣性で追いかける側の進捗。
        rAFループの中で renderProgress を targetProgress へ毎フレーム収束させる。
      */
      this.smoothing = this.parseSmoothing();
      this.targetProgress = 0;
      this.renderProgress = 0;
      this.hasRenderedOnce = false;

      /* data-immersive="true" のとき、ストーリー終了までヘッダー等を表示しない */
      this.immersive = this.dataset.immersive === 'true';
      this.immersiveOn = false;
      this.immersiveFinished = false;
      this.headerGroup = document.querySelector('#header-group');
      this.headerGroupDisplay = this.headerGroup?.style.getPropertyValue('display') || '';
      this.headerGroupDisplayPriority = this.headerGroup?.style.getPropertyPriority('display') || '';
      if (this.immersive) this.setImmersiveState(true);
      if (!this.immersive) document.documentElement.classList.remove('scroll-story-immersive');

      this.requestTick = this.requestTick.bind(this);
      this.tick = this.tick.bind(this);
      this.onResize = this.onResize.bind(this);

      this.resizeCanvas();
      this.updateHeaderOffset();
      this.watchHeaderOffset();

      /* 画面外では描画を止める */
      this.observer = new IntersectionObserver(
        (entries) => {
          this.isVisible = entries.some((entry) => entry.isIntersecting);
          /*
            ステップモードで画面外に出たら stepIndex をリセットする。
            上からまだ来ていない(rect.top > 0)なら次に入るのは先頭から、
            すでに上へ抜けた(rect.bottom < innerHeight)なら最後のステップから
            始まるようにしておく(下から戻ってきたときに最初からにならないため)。
          */
          if (this.stepMode && !this.isVisible) {
            const rect = entries[0] ? entries[0].boundingClientRect : this.track.getBoundingClientRect();
            if (rect.top > 0) {
              this.stepIndex = 0;
            } else if (rect.bottom < innerHeight) {
              this.stepIndex = this.stops.length - 1;
            }
            this.updateStepDots();
          }
          this.refreshStepDotsVisibility();
          this.refreshScrollCueVisibility();
          this.requestTick();
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this.track);
      /* ステップモードの終了は最終ステップの描画完了で判定する。 */
      if (this.immersive && this.endMarker && !this.stepMode) {
        this.endObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.finishImmersiveStory();
        });
        this.endObserver.observe(this.endMarker);
      }

      /*
        Horizonは html/body ではなく div.page-wrapper をスクロールさせるため、
        window の scroll イベントは発火しない。scrollイベントはバブルしないが
        キャプチャフェーズなら拾えるので、どの要素がスクロールしても検知できる。
      */
      addEventListener('scroll', this.requestTick, { passive: true, capture: true });
      addEventListener('resize', this.onResize);
      this.setupStepMode();
      this.setupStepDots();
      this.setupScrollCue();
      this.restoreEditorScrollPosition();

      this.loadFramesStaggered();
      this.requestTick();
    }

    disconnectedCallback() {
      this.saveEditorState();
      removeEventListener('scroll', this.requestTick, { capture: true });
      removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
      this.endObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.immersiveOn) {
        document.documentElement.classList.remove('scroll-story-immersive');
        this.immersiveOn = false;
      }
      document.documentElement.classList.remove('scroll-story-complete');
      this.setHeaderGroupHidden(false);
      this.setHeaderGroupVeil(false);
      this.stepDots?.remove();
      this.scrollCue?.remove();
      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('wheel', this.onWheel, { passive: false });
        this.scrollContainer.removeEventListener('touchstart', this.onTouchStart, { passive: true });
        this.scrollContainer.removeEventListener('touchmove', this.onTouchMove, { passive: false });
        this.scrollContainer.removeEventListener('touchend', this.onTouchEnd, { passive: true });
        this.scrollContainer.removeEventListener('keydown', this.onKeydown);
      }
    }

    /*
      テーマエディタでは設定を1つ変えるたびにセクションDOMが差し替えられる。
      ステップモードの現在地だけを短時間保持し、画像サイズや座標を調整しても
      プレビューが先頭フレームへ戻らないようにする。本番閲覧では保存しない。
    */
    editorStepStorageKey() {
      if (this.dataset.designMode !== 'true' || !this.dataset.sectionId) return null;
      return `scroll-story-editor-step:${this.dataset.sectionId}`;
    }

    getEditorState() {
      const key = this.editorStepStorageKey();
      if (!key) return null;
      try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        if (!saved || Date.now() - saved.savedAt > 10 * 60 * 1000) return null;
        return saved;
      } catch {
        return null;
      }
    }

    restoreEditorStepIndex() {
      if (!this.stepMode || !this.editorState) return 0;
      return clamp(Number(this.editorState.stepIndex) || 0, 0, this.stops.length - 1);
    }

    restoreEditorScrollPosition() {
      const scrollTop = Number(this.editorState?.scrollTop);
      if (!this.scrollContainer || !Number.isFinite(scrollTop)) return;
      /* Shopifyがセクション差し替え後にプレビューを先頭へ戻すより後に復元する。 */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setScrollTopInstant(this.scrollContainer, scrollTop);
          this.requestTick();
        });
      });
    }

    saveEditorState() {
      const key = this.editorStepStorageKey();
      if (!key) return;
      try {
        const scrollContainer = this.scrollContainer || document.querySelector('.page-wrapper') || document.scrollingElement;
        sessionStorage.setItem(key, JSON.stringify({
          stepIndex: this.stepIndex,
          scrollTop: scrollContainer?.scrollTop || 0,
          savedAt: Date.now(),
        }));
      } catch {
        // ストレージが利用できない環境では、従来どおり先頭から開始する。
      }
    }

    /* data-smoothing (0.01〜1.0) を読む。未指定・不正値なら DEFAULT_SMOOTHING */
    parseSmoothing() {
      const raw = Number(this.dataset.smoothing);
      if (!Number.isFinite(raw) || raw < 0.01 || raw > 1) return DEFAULT_SMOOTHING;
      return raw;
    }

    /* data-blend-start / data-blend-end (0〜1) を読む。未指定・不正値なら fallback */
    parseBlendValue(raw, fallback) {
      const value = Number(raw);
      return Number.isFinite(value) ? clamp(value, 0, 1) : fallback;
    }

    /* data-blend-mode を読む。source-over / screen / lighter / mask 以外は source-over */
    parseBlendMode(raw) {
      const allowed = ['source-over', 'screen', 'lighter', 'mask'];
      return allowed.includes(raw) ? raw : 'source-over';
    }

    /* data-mask-low / data-mask-high (0〜1) を読む。不正(未指定・範囲外・low>=high)なら既定値(0.2/0.7)にフォールバック */
    parseMaskRange(rawLow, rawHigh) {
      const low = Number(rawLow);
      const high = Number(rawHigh);
      const validLow = Number.isFinite(low) ? clamp(low, 0, 1) : 0.2;
      const validHigh = Number.isFinite(high) ? clamp(high, 0, 1) : 0.7;
      if (validLow >= validHigh) return { maskLow: 0.2, maskHigh: 0.7 };
      return { maskLow: validLow, maskHigh: validHigh };
    }

    /*
      data-step-duration (ms) を読む。
      "1400" のような単一値なら全ての遷移で共通、
      "1800,1200" のようなカンマ区切りなら段階ごとに別の長さを使う。
      不正値なら DEFAULT_STEP_DURATION。
    */
    parseStepDuration() {
      const list = String(this.dataset.stepDuration || '')
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => Number.isFinite(v) && v > 0);
      return list.length ? list : [DEFAULT_STEP_DURATION];
    }

    /*
      どの区間の遷移かで所要時間を選ぶ。
      ステップ0→1 は1つ目、1→2 は2つ目。戻るときも同じ区間の値を使う。
      指定が足りなければ最後の値を使い回す。
    */
    durationFor(fromIndex, toIndex) {
      const seg = Math.min(fromIndex, toIndex);
      return this.stepDuration[Math.min(seg, this.stepDuration.length - 1)];
    }

    /* data-step-stops (カンマ区切りの0〜100) を読む。要素2個未満・不正なら既定値[0, 0.55, 1] */
    parseStepStops() {
      const raw = this.dataset.stepStops;
      if (!raw) return DEFAULT_STEP_STOPS;
      const parsed = raw
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((n) => Number.isFinite(n));
      if (parsed.length < 2) return DEFAULT_STEP_STOPS;
      return parsed.map((n) => clamp(n / 100, 0, 1));
    }

    /* A/Bどちらのシーケンスも同じ形で扱えるようにするための入れ物 */
    createSequence({ frameCount, frameStart, framePad, frameTemplate }) {
      return {
        frameCount,
        frameStart,
        framePad,
        frameTemplate,
        frames: new Array(frameCount),
        loadedCount: 0,
      };
    }

    frameUrl(seq, index) {
      const number = String(seq.frameStart + index).padStart(seq.framePad, '0');
      return seq.frameTemplate.replace('{i}', number);
    }

    /*
      先頭から順に、同時 LOAD_CONCURRENCY 本で読み込む。
      1枚目が届いた時点で描画が始まり、残りは後追いで埋まっていく。
      seq には seqA / seqB のどちらも渡せる。onProgress は読み込み済み枚数を毎回受け取る。
    */
    loadFrames(seq, onProgress) {
      if (!seq || seq.frameCount <= 0) return;
      let next = 0;
      const loadOne = () => {
        if (next >= seq.frameCount) return;
        const index = next++;
        const img = new Image();
        img.decoding = 'async';
        const done = () => {
          seq.loadedCount++;
          this.updateStatus();
          this.requestTick();
          onProgress?.(seq.loadedCount);
          loadOne();
        };
        img.onload = () => {
          seq.frames[index] = img;
          done();
        };
        img.onerror = done;
        img.src = this.frameUrl(seq, index);
      };
      for (let i = 0; i < LOAD_CONCURRENCY; i++) loadOne();
    }

    /*
      seqA(最初に見えるシーケンス)とseqBを同時に読み始めると、帯域を奪い合って
      両方とも遅くなる。ユーザーが最初に見るのはAなので、Aが序盤まで読み終わって
      から初めてBを読み始める。ただしAの読み込みが遅い回線でもBが永遠に始まらない
      ことがないよう、一定時間経ったら強制的にBも開始するフォールバックを設ける。
    */
    loadFramesStaggered() {
      const bootstrap = Math.min(BOOTSTRAP_FRAME_COUNT, this.seqA.frameCount);
      let bStarted = false;
      const startB = () => {
        if (bStarted) return;
        bStarted = true;
        this.loadFrames(this.seqB);
      };
      this.loadFrames(this.seqA, (loaded) => {
        if (loaded >= bootstrap) {
          startB();
        }
      });
      if (bootstrap <= 0) {
        startB();
      }
      setTimeout(startB, BOOTSTRAP_TIMEOUT_MS);
    }

    updateStatus() {
      if (!this.status) return;
      const totalCount = this.seqA.frameCount + this.seqB.frameCount;
      const totalLoaded = this.seqA.loadedCount + this.seqB.loadedCount;
      if (totalLoaded >= totalCount) {
        this.status.hidden = true;
        return;
      }
      this.status.hidden = false;
      this.status.textContent = `${totalLoaded} / ${totalCount}`;
    }

    /* 目的のフレームが未ロードなら、読み込み済みで最も近いものを代わりに描く */
    nearestLoaded(seq, index) {
      if (seq.frames[index]) return seq.frames[index];
      for (let distance = 1; distance < seq.frameCount; distance++) {
        if (seq.frames[index - distance]) return seq.frames[index - distance];
        if (seq.frames[index + distance]) return seq.frames[index + distance];
      }
      return null;
    }

    onResize() {
      this.resizeCanvas();
      this.updateHeaderOffset();
      this.requestTick();
    }

    /*
      ヘッダー/広告バー(.shopify-section-group-header-group)の高さを測り、
      --scroll-story-header-offset としてCSSに渡す。ストーリー中はステージを
      その分だけ上へ広げ、ページの先頭からヘッダーなしで始めるために使う。
      ヘッダーはstickyで位置は動くが高さは変わらないため、tick()からは呼ばない。
    */
    updateHeaderOffset() {
      if (!this.immersive) {
        this.style.setProperty('--scroll-story-header-offset', '0px');
        return;
      }
      /*
        ヘッダーの実際のレイアウト領域は #header-group。個別のセクション用class
        だけでは親の余白を取りこぼし、白い帯が残るため、ラッパー全体を測る。
      */
      const headerGroup = document.querySelector('#header-group');
      const height = headerGroup?.getBoundingClientRect().height || 0;
      this.style.setProperty('--scroll-story-header-offset', `${height}px`);
    }

    /* CSSクラスではなく実際のラッパーを畳み、ヘッダーの白いレイアウト領域をなくす。 */
    setHeaderGroupHidden(hidden) {
      if (!this.headerGroup) return;
      if (hidden) {
        this.headerGroup.style.setProperty('display', 'none', 'important');
      } else if (this.headerGroupDisplay) {
        this.headerGroup.style.setProperty('display', this.headerGroupDisplay, this.headerGroupDisplayPriority);
      } else {
        this.headerGroup.style.removeProperty('display');
      }
    }

    getScrollContainer() {
      return this.scrollContainer || document.querySelector('.page-wrapper') || document.scrollingElement;
    }

    /*
      #header-group は sections/header.liquid の
      `#header-group:has(#header-component[sticky]) { display: contents; }`
      によって「ボックスを持たない要素」になる。
      そのため #header-group 自身への visibility 指定や getBoundingClientRect()
      は当てにならない(rectは常に0)。実際に描画されるのは子要素なので、
      隠す・測るはすべて子要素に対して行う。
    */
    headerGroupBoxes() {
      if (!this.headerGroup) return [];
      return Array.from(this.headerGroup.children).filter((el) => el instanceof HTMLElement);
    }

    /* 復帰の瞬間だけ、ヘッダー群の実ボックスを直接不可視にする保険。 */
    setHeaderGroupVeil(on) {
      for (const el of this.headerGroupBoxes()) {
        if (on) el.style.setProperty('visibility', 'hidden', 'important');
        else el.style.removeProperty('visibility');
      }
    }

    /*
      時間で解除すると、補正が滑ったり中断されたときに露出する。
      「ヘッダー群が実際に画面外へ出た」フレームで解除する。
      ユーザーが上へスクロールし直した場合と、保険が張り付かないよう
      上限時間を超えた場合も解除する。
    */
    releaseHeaderGroupVeilWhenOffscreen() {
      const container = this.getScrollContainer();
      const baseScrollTop = container ? container.scrollTop : 0;
      const deadline = performance.now() + 1500;
      const check = () => {
        const boxes = this.headerGroupBoxes();
        const offscreen = boxes.length === 0 || boxes.every((el) => el.getBoundingClientRect().bottom <= 0);
        const scrolledUp = container ? container.scrollTop < baseScrollTop - 2 : false;
        if (offscreen || scrolledUp || performance.now() >= deadline) {
          this.setHeaderGroupVeil(false);
          return;
        }
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    }

    setImmersiveState(active) {
      if (active === this.immersiveOn) return;
      const header = document.querySelector('#header-component');
      if (!active && header) header.dataset.stickyState = 'idle';

      /* 表示状態切り替え前後で自分の画面内位置がどれだけズレたか測り、レイアウト
         シフトによる見た目のジャンプをスクロール位置の補正で打ち消す。 */
      const beforeTop = !active ? this.getBoundingClientRect().top : null;
      if (!active) this.setHeaderGroupVeil(true);
      this.setHeaderGroupHidden(active);
      document.documentElement.classList.toggle('scroll-story-immersive', active);
      this.immersiveOn = active;
      if (!active && beforeTop !== null) {
        const afterTop = this.getBoundingClientRect().top;
        const delta = afterTop - beforeTop;
        const container = this.getScrollContainer();
        /*
          ここが今回の点滅の主因。
          .page-wrapper には scroll-behavior: smooth が効いているため、
          `container.scrollTop += delta` はアニメーションになる。
          その数百msの間、復帰したヘッダー群(緑のアナウンスバー＋白いヘッダー)が
          画面上部に見えたまま滑り、さらにユーザーのホイール操作がこの
          プログラム的スムーススクロールを中断・再開させるため、
          緑と白が交互に何度も現れる。必ず即時に補正する。
        */
        if (delta !== 0 && container) setScrollTopInstant(container, container.scrollTop + delta);
        this.releaseHeaderGroupVeilWhenOffscreen();
      }
      if (active) this.setHeaderGroupVeil(false);
    }

    /* ヘッダーの遅延描画・テーマエディタでの再描画後にも高さを取り直す。 */
    watchHeaderOffset() {
      if (!this.immersive) return;
      const headerGroup = document.querySelector('#header-group');
      if (!headerGroup) return;
      this.headerResizeObserver = new ResizeObserver(() => this.updateHeaderOffset());
      this.headerResizeObserver.observe(headerGroup);
      requestAnimationFrame(() => this.updateHeaderOffset());
      setTimeout(() => this.updateHeaderOffset(), 300);
    }

    resizeCanvas() {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      const width = Math.round(rect.width * dpr);
      const height = Math.round(rect.height * dpr);
      if (width === 0 || height === 0) return;
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
        this.lastKey = null; // サイズが変わったので描き直す

        if (this.modelCanvas) {
          this.modelCanvas.width = width;
          this.modelCanvas.height = height;
        }
        if (this.maskCanvas) {
          const MASK_MAX_WIDTH = 384;
          const maskWidth = Math.min(MASK_MAX_WIDTH, width);
          const maskHeight = Math.max(1, Math.round(maskWidth * (height / width)));
          this.maskCanvas.width = maskWidth;
          this.maskCanvas.height = maskHeight;
          this.maskCacheIndex = -1; // マスクのキャッシュを破棄し、次回作り直す
        }
      }
    }

    /* スクロール/リサイズ/可視化のたびに呼ぶ。ループが止まっていれば再開する */
    requestTick() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(this.tick);
    }

    /*
      ステップモードの入力(wheel/touch/keydown)を、スクロールコンテナに
      { passive: false } で登録する。connectedCallback からのみ呼ぶ。
    */
    setupStepMode() {
      if (!this.stepMode) return;
      this.scrollContainer = document.querySelector('.page-wrapper') || document.scrollingElement;
      if (!this.scrollContainer) return;

      this.lastWheelTime = 0;
      this.touchStartY = null;
      this.touchHandled = false;

      this.onWheel = this.onWheel.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.scrollContainer.addEventListener('wheel', this.onWheel, { passive: false });
      this.scrollContainer.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.scrollContainer.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.scrollContainer.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.scrollContainer.addEventListener('keydown', this.onKeydown);
    }

    /*
      ステップの現在地を示すドットインジケーターを右端に生成する。
      要素数はステップ数(this.stops.length)に合わせて動的に作る。
      コンポーネント内ではなく document.body に position:fixed で追加する
      (immersive解除でヘッダーが復帰するなどのレイアウト変化に影響されないため)。
    */
    setupStepDots() {
      if (!this.stepMode || this.stops.length < 2) return;
      this.stepDots = document.createElement('div');
      this.stepDots.className = 'scroll-story__progress-dots';
      this.stepDots.setAttribute('role', 'tablist');
      this.stepDots.setAttribute('aria-label', 'ストーリーの進行状況');
      this.stepDotButtons = this.stops.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'scroll-story__progress-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${index + 1} / ${this.stops.length}`);
        dot.addEventListener('click', () => this.jumpToStep(index));
        this.stepDots.appendChild(dot);
        return dot;
      });
      document.body.appendChild(this.stepDots);
      this.updateStepDots();
      this.refreshStepDotsVisibility();
    }

    /* 現在の stepIndex に合わせて、どのドットをアクティブ表示にするか更新する */
    updateStepDots() {
      if (!this.stepDotButtons) return;
      this.stepDotButtons.forEach((dot, index) => {
        const active = index === this.stepIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    /*
      ドットの表示/非表示。ストーリーが画面内にあり、かつ(immersiveの場合)
      まだ最後まで抜けきっていない間だけ表示する。抜けて通常のページ内容に
      入ったらフェードアウトする(headerの表示制御と同じ考え方)。
    */
    refreshStepDotsVisibility() {
      if (!this.stepDots) return;
      const finished = this.immersive && this.immersiveFinished;
      this.stepDots.classList.toggle('is-visible', this.isVisible && !finished);
    }

    /*
      最初のステップ(宝石クローズアップ)でだけ表示する、下スクロールを促す
      アニメーション。縦の光の筋を発光ドットが流れ落ちる演出+「SCROLL」の文字。
      ドットインジケーターと同様、document.body直下に固定配置する。
    */
    setupScrollCue() {
      if (!this.stepMode) return;
      this.scrollCue = document.createElement('div');
      this.scrollCue.className = 'scroll-story__scroll-cue';
      this.scrollCue.setAttribute('aria-hidden', 'true');
      this.scrollCue.innerHTML =
        '<span class="scroll-story__scroll-cue-label">Scroll</span>' +
        '<span class="scroll-story__scroll-cue-line"><span class="scroll-story__scroll-cue-dot"></span></span>';
      document.body.appendChild(this.scrollCue);
      this.refreshScrollCueVisibility();
    }

    /* 最初のステップに静止している間だけ見せる。遷移中・他ステップ・画面外では隠す。 */
    refreshScrollCueVisibility() {
      if (!this.scrollCue) return;
      const visible = this.isVisible && this.stepIndex === 0 && !this.stepAnimating;
      this.scrollCue.classList.toggle('is-visible', visible);
    }

    /*
      ドットのクリック用。stepAdvance は隣接ステップにしか進めないため、
      任意のステップへ直接ジャンプできる別ロジックを用意する。
    */
    jumpToStep(index) {
      if (!this.stepMode) return;
      const target = clamp(index, 0, this.stops.length - 1);
      if (target === this.stepIndex && !this.stepAnimating) return;
      this.stepDurationCurrent = this.durationFor(this.stepIndex, target);
      this.stepFrom = this.renderProgress;
      this.stepIndex = target;
      this.stepTo = this.stops[target];
      this.stepStart = performance.now();
      this.stepAnimating = true;
      this.updateStepDots();
      this.requestTick();
    }

    /*
      1ステップ分の入力(方向 dir = +1/-1)を処理する共通ロジック。
      戻り値 true = 呼び出し側は preventDefault() すべき(セクションが画面を占有し、
      進める余地があった)。false = 何もしない(＝通常のスクロールを通す)。
      skipAdvance が true のときは preventDefault はするが stepIndex は進めない
      (wheelの連続イベント間引き用)。
    */
    stepAdvance(dir, skipAdvance) {
      const rect = this.track.getBoundingClientRect();
      const covering = rect.top <= 1 && rect.bottom >= innerHeight - 1;
      if (!covering) return false;

      /*
        完了後はヘッダーを表示したまま通常スクロールを許可する。
        ストーリー画像を覆った位置でさらに上へ進もうとした入力だけを捕捉し、
        その瞬間にヘッダーを隠して最終ステップから逆行を開始する。
      */
      if (this.immersiveFinished) {
        if (dir >= 0 || !this.resumeImmersiveStory(rect)) return false;
        skipAdvance = false;
      }

      const next = this.stepIndex + dir;
      if (next < 0 || next >= this.stops.length) {
        /*
          最終モデルへの遷移中に発生する同一ホイール操作の続きは、
          ページスクロールへ漏らさない。遷移完了後の次の操作だけを
          ページ下部へ通すため、モデル全体を必ず見せてから抜けられる。
        */
        if (dir > 0 && this.stepAnimating && this.stepIndex === this.stops.length - 1) return true;
        if (dir > 0 && this.stepIndex === this.stops.length - 1) this.finishImmersiveStory();
        return false;
      }

      /* 遷移中(stepAnimating)は連打を無視する。入力を捨てる判定はこれで行う */
      if (!skipAdvance && !this.stepAnimating) {
        this.stepDurationCurrent = this.durationFor(this.stepIndex, next);
        this.stepIndex = next;
        this.stepFrom = this.renderProgress;
        this.stepTo = this.stops[next];
        this.stepStart = performance.now();
        this.stepAnimating = true;
        this.updateStepDots();
        this.requestTick();
      }
      return true;
    }

    /*
      最終フレームを表示し終えたら、没入表示を解除してヘッダーの display を戻す。
      以前は header.dataset.stickyState / scrollDirection も強制的に 'active'/'up' に
      していたが、これは header.js 自身のスクロール検知ロジック(#updateScrollState)
      と競合する。下スクロール中は次の scroll イベント(数十ms後)で header.js が
      実際の方向に基づいて stickyState を 'idle' に上書きし、
      .header[data-sticky-state='idle'] { opacity: 0; } により、
      「一瞬 sticky 表示 → 即座に opacity:0 で消える」というチラつきになっていた。
      強制上書きをやめ、header.js 本来の状態遷移に任せることでこれを解消する。
    */
    finishImmersiveStory() {
      if (this.immersiveFinished) return;
      if (this.immersive) {
        this.immersiveFinished = true;
        document.documentElement.classList.add('scroll-story-complete');
        this.setImmersiveState(false);
        this.setHeaderGroupVeil(false);
        this.refreshStepDotsVisibility();
      }
    }

    /*
      ストーリー終了後、下のコンテンツから上へ戻り、ステージが画面を覆った状態で
      さらに上方向の入力が入ったときだけ没入表示を再開する。現在の最終ステップは
      維持するため、その入力で最後のモデル画像から先頭画像へ逆行できる。
    */
    resumeImmersiveStory(rect) {
      if (!this.immersive || !this.immersiveFinished) return false;
      const covering = rect.top <= 1 && rect.bottom >= innerHeight - 1;
      if (!covering) return false;

      const container = this.getScrollContainer();
      const beforeTop = this.getBoundingClientRect().top;
      document.documentElement.classList.remove('scroll-story-complete');
      this.immersiveFinished = false;
      this.setImmersiveState(true);
      this.updateHeaderOffset();

      /* ヘッダー領域を再び畳んだ際のレイアウト移動を相殺する。 */
      const delta = this.getBoundingClientRect().top - beforeTop;
      if (delta !== 0 && container) setScrollTopInstant(container, container.scrollTop + delta);

      this.updateStepDots();
      this.refreshStepDotsVisibility();
      this.refreshScrollCueVisibility();
      return true;
    }

    onWheel(e) {
      if (!this.stepMode) return;
      const dir = e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0;
      if (dir === 0) return;

      const now = Date.now();
      const isSameGesture = now - this.lastWheelTime < WHEEL_GESTURE_WINDOW;
      this.lastWheelTime = now;

      if (this.stepAdvance(dir, isSameGesture)) e.preventDefault();
    }

    onTouchStart(e) {
      if (!this.stepMode) return;
      const touch = e.touches[0];
      this.touchStartY = touch ? touch.clientY : null;
      this.touchHandled = false;
    }

    onTouchMove(e) {
      if (!this.stepMode || this.touchStartY === null || this.touchHandled) return;
      const touch = e.touches[0];
      if (!touch) return;

      const delta = this.touchStartY - touch.clientY;
      if (Math.abs(delta) <= TOUCH_STEP_THRESHOLD) return;

      this.touchHandled = true;
      const dir = delta > 0 ? 1 : -1;
      if (this.stepAdvance(dir, false)) e.preventDefault();
    }

    onTouchEnd() {
      this.touchStartY = null;
      this.touchHandled = false;
    }

    onKeydown(e) {
      if (!this.stepMode) return;
      const target = e.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (target && target.isContentEditable)) return;

      let dir = 0;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ' || e.code === 'Space') dir = 1;
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') dir = -1;
      else return;

      if (this.stepAdvance(dir, false)) e.preventDefault();
    }

    tick() {
      this.rafId = 0;

      const rect = this.track.getBoundingClientRect();
      if (this.stepMode) {
        /*
          ステップモードは慣性補間を使わず、時間ベースのイージングで
          renderProgress を直接動かす(stepAdvance が遷移を開始する)。
          遷移中でなければ現在のステップへ即座に固定する。
        */
        if (this.stepAnimating) {
          const t = clamp((performance.now() - this.stepStart) / (this.stepDurationCurrent || this.stepDuration[0]), 0, 1);
          const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // ease-in-out cubic
          this.renderProgress = this.stepFrom + (this.stepTo - this.stepFrom) * e;
          this.targetProgress = this.stepTo;
          if (t >= 1) {
            this.renderProgress = this.stepTo;
            this.stepAnimating = false;
          }
        } else {
          this.targetProgress = this.stops[this.stepIndex];
          this.renderProgress = this.targetProgress;
        }
        this.refreshScrollCueVisibility();
      } else {
        const scrollable = rect.height - innerHeight;
        this.targetProgress = scrollable > 0 ? clamp(-rect.top / scrollable, 0, 1) : 0;
      }

      if (this.immersive) {
        /*
          読み込み直後からストーリー末尾を通過するまで、ヘッダー領域ごと除去する。
          表示復帰によるレイアウト変化でON/OFFを繰り返さないよう、末尾を抜けた後は
          ストーリー先頭まで戻るまで表示状態を維持する。
        */
        if (!this.immersiveFinished && rect.bottom <= 0) this.finishImmersiveStory();
        const immersiveNow = !this.immersiveFinished;
        this.setImmersiveState(immersiveNow);
        this.refreshStepDotsVisibility();
      }

      if (!this.stepMode) {
        /*
          慣性を効かせるのは「画面内で、2回目以降」のときだけ。
          画面外や初回は係数1(=即追従)にして値を目標に一致させておく。
          こうしておくと、画面外に出ている間に中途半端な値が取り残されず、
          再入場したときは常に正しいフレームから始まる。
        */
        const animate = this.isVisible && this.hasRenderedOnce && !this.reduced;
        const smoothing = animate ? this.smoothing : 1;
        this.renderProgress += (this.targetProgress - this.renderProgress) * smoothing;
        if (Math.abs(this.targetProgress - this.renderProgress) < SETTLE_EPSILON) {
          this.renderProgress = this.targetProgress;
        }
      }
      this.hasRenderedOnce = true;

      /* 中間ステップに静止している間だけ、台形面の反射を時間ベースで繰り返す。 */
      let reflectionPhase = null;
      const reflectionActive = this.stepMode && this.isVisible && this.stepIndex === 1 && !this.stepAnimating;
      if (reflectionActive) {
        const now = performance.now();
        if (!this.reflectionCycleStart) this.reflectionCycleStart = now;
        const cycleDuration = 2600;
        const sweepDuration = 1700;
        const elapsed = (now - this.reflectionCycleStart) % cycleDuration;
        if (elapsed < sweepDuration) reflectionPhase = elapsed / sweepDuration;
      } else {
        this.reflectionCycleStart = 0;
      }

      /* 描画は画面内のときだけ。画面外では上の値の同期だけ行う */
      if (this.isVisible) {
        this.style.setProperty('--scroll-story-progress', this.renderProgress.toFixed(4));
        this.draw(this.renderProgress, reflectionPhase);
        this.updateCaptions(this.renderProgress);
      }

      /* まだ目標に吸着していなければループを継続する(ステップモードは stepAnimating で判定) */
      const shouldContinue = this.stepMode
        ? this.stepAnimating || reflectionActive
        : this.renderProgress !== this.targetProgress;
      if (shouldContinue) {
        this.rafId = requestAnimationFrame(this.tick);
      }
    }

    /* 指定した ctx/幅/高さ に対して cover(object-fit: cover相当)方式で img を描く */
    drawCoverInto(ctx, cw, ch, img) {
      const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }

    /*
      宝石フレーム(gemImg)の輝度からマスクを作り this.maskCanvas に書き込む。
      宝石フレームが変わったときだけ呼ぶこと(毎フレームのgetImageDataは重い)。
    */
    buildMask(gemImg) {
      const mw = this.maskCanvas.width;
      const mh = this.maskCanvas.height;
      const ctx = this.maskCtx;
      ctx.clearRect(0, 0, mw, mh);
      this.drawCoverInto(ctx, mw, mh, gemImg);

      const imageData = ctx.getImageData(0, 0, mw, mh);
      const data = imageData.data;
      const range = this.maskHigh - this.maskLow;
      for (let i = 0; i < data.length; i += 4) {
        const lum = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
        const alpha = clamp((lum - this.maskLow) / range, 0, 1) * 255;
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = alpha;
      }
      ctx.putImageData(imageData, 0, 0);
    }

    /*
      中間ステップに静止している間だけ、画像内の台形状のカット面を
      斜めの反射光が繰り返し横切る。座標は確認画像の赤枠に合わせた比率。
    */
    drawCutReflection({ imgB, phase }) {
      if (!imgB || phase === null) return;
      const { width: w, height: h } = this.canvas;
      const scale = Math.max(w / imgB.naturalWidth, h / imgB.naturalHeight);
      const drawnWidth = imgB.naturalWidth * scale;
      const drawnHeight = imgB.naturalHeight * scale;
      const offsetX = (w - drawnWidth) / 2;
      const offsetY = (h - drawnHeight) / 2;
      const point = (x, y) => [offsetX + drawnWidth * x, offsetY + drawnHeight * y];
      const topLeft = point(0.285, 0.39);
      const topRight = point(0.61, 0.255);
      const bottomRight = point(0.589, 0.798);
      const bottomLeft = point(0.292, 0.645);
      const left = Math.min(topLeft[0], bottomLeft[0]);
      const right = Math.max(topRight[0], bottomRight[0]);
      const angle = -18 * (Math.PI / 180);
      const halfWidth = Math.max(24, w * 0.048);
      const tiltOffset = (h / 2) * Math.tan(Math.abs(angle));
      const start = left - tiltOffset - halfWidth;
      const end = right + tiltOffset + halfWidth;
      const travelPhase = smoothstep(0, 1, phase);
      const center = start + travelPhase * (end - start);
      const gradient = this.ctx.createLinearGradient(-halfWidth, 0, halfWidth, 0);
      gradient.addColorStop(0, 'rgba(245, 250, 255, 0)');
      gradient.addColorStop(0.18, 'rgba(245, 250, 255, 0.04)');
      gradient.addColorStop(0.38, 'rgba(250, 253, 255, 0.16)');
      gradient.addColorStop(0.47, 'rgba(255, 255, 255, 0.58)');
      gradient.addColorStop(0.505, 'rgba(255, 255, 255, 0.98)');
      gradient.addColorStop(0.54, 'rgba(255, 255, 255, 0.22)');
      gradient.addColorStop(0.61, 'rgba(255, 255, 255, 0.6)');
      gradient.addColorStop(0.65, 'rgba(250, 253, 255, 0.14)');
      gradient.addColorStop(0.78, 'rgba(245, 250, 255, 0.05)');
      gradient.addColorStop(1, 'rgba(245, 250, 255, 0)');

      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.moveTo(topLeft[0], topLeft[1]);
      this.ctx.lineTo(topRight[0], topRight[1]);
      this.ctx.lineTo(bottomRight[0], bottomRight[1]);
      this.ctx.lineTo(bottomLeft[0], bottomLeft[1]);
      this.ctx.closePath();
      this.ctx.clip();
      this.ctx.globalCompositeOperation = 'screen';
      const fadeIn = smoothstep(0, 0.12, phase);
      const fadeOut = 1 - smoothstep(0.88, 1, phase);
      const shimmer = 0.94 + Math.sin(phase * Math.PI * 8) * 0.06;
      this.ctx.globalAlpha = Math.min(fadeIn, fadeOut) * 0.27 * shimmer;
      this.ctx.translate(center, h / 2);
      this.ctx.rotate(angle);
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(-halfWidth, -h, halfWidth * 2, h * 2);
      this.ctx.restore();
    }

    /*
      blendMode === 'mask' のときの描画。
      宝石フレームの輝度をマスクにして、明るい面(カット面)にだけモデルを重ねる。
      blendAlpha が1に近づいたら(fullyB)、最終的にはマスクなしでモデル全景へ切り替える。
    */
    drawMaskBlend({ imgA, imgB, blendAlpha, fullyB, aIndex, reflectionPhase }) {
      const { width: cw, height: ch } = this.canvas;

      if (imgA) {
        this.drawCoverInto(this.ctx, cw, ch, imgA);
      }

      if (blendAlpha > 0 && imgB) {
        if (fullyB) {
          this.ctx.globalAlpha = 1;
          this.ctx.globalCompositeOperation = 'source-over';
          this.drawCoverInto(this.ctx, cw, ch, imgB);
        } else {
          if (imgA && this.maskCacheIndex !== aIndex) {
            this.buildMask(imgA);
            this.maskCacheIndex = aIndex;
          }

          const mw = this.modelCanvas.width;
          const mh = this.modelCanvas.height;
          this.modelCtx.clearRect(0, 0, mw, mh);
          this.drawCoverInto(this.modelCtx, mw, mh, imgB);

          this.modelCtx.globalCompositeOperation = 'destination-in';
          this.modelCtx.drawImage(this.maskCanvas, 0, 0, mw, mh);
          this.modelCtx.globalCompositeOperation = 'source-over';

          this.ctx.globalAlpha = blendAlpha;
          this.ctx.globalCompositeOperation = 'source-over';
          this.ctx.drawImage(this.modelCanvas, 0, 0);
        }
      }

      this.drawCutReflection({ imgB, phase: reflectionPhase });

      this.ctx.globalAlpha = 1;
      this.ctx.globalCompositeOperation = 'source-over';
    }

    /*
      progress(0〜1)を受け取り、Aシーケンスを描いた上で、設定があれば
      Bシーケンス(鏡面反射から浮かび上がる第2シーケンス)をクロスフェードで重ねる。
      seqB が空(frameCount === 0)なら従来どおりAをprogressそのままで描くだけ。
    */
    draw(progress, reflectionPhase = null) {
      const hasB = this.seqB.frameCount > 0;

      let blendAlpha = 0;
      let aProgress = progress;
      let bProgress = 0;
      if (hasB) {
        blendAlpha = smoothstep(this.blendStart, this.blendEnd, progress);
        /*
          Aは「重なり始める位置」で最終フレーム(カット面の最大ズーム)に到達し、
          そこから先は止まったまま。その静止した面の上でBが立ち上がるので、
          「寄り切った面からモデルが出てくる」流れになる。
        */
        const aEnd = this.blendStart > 0 ? this.blendStart : this.blendEnd;
        aProgress = clamp(progress / aEnd, 0, 1);
        bProgress = clamp((progress - this.blendStart) / (1 - this.blendStart), 0, 1);
      }

      const aIndex = Math.round(aProgress * Math.max(this.seqA.frameCount - 1, 0));
      const bIndex = hasB ? Math.round(bProgress * Math.max(this.seqB.frameCount - 1, 0)) : -1;

      const imgA = this.nearestLoaded(this.seqA, aIndex);
      const imgB = hasB ? this.nearestLoaded(this.seqB, bIndex) : null;

      const fullyB = hasB && blendAlpha >= 0.999;

      /*
        描くべき画像がまだ読み込めていないうちは lastKey を更新せずに諦める。
        ここでキーを記録してしまうと、後から画像が届いても
        「同じキー＝描画済み」と判定されて二度と描かれなくなる。
      */
      if (!fullyB && !imgA) return;
      if (hasB && blendAlpha > 0 && !imgB) return;

      const reflectionKey = reflectionPhase === null ? 'off' : reflectionPhase.toFixed(3);
      const key = `${aIndex}|${bIndex}|${blendAlpha.toFixed(3)}|${reflectionKey}`;
      if (key === this.lastKey) return;
      this.lastKey = key;

      const { width: cw, height: ch } = this.canvas;
      /* object-fit: cover 相当。中央でトリミングする */
      const drawCover = (img) => {
        const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
        const w = img.naturalWidth * scale;
        const h = img.naturalHeight * scale;
        this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      };

      if (!hasB) {
        if (imgA) drawCover(imgA);
        return;
      }

      if (this.blendMode === 'mask') {
        this.drawMaskBlend({ imgA, imgB, blendAlpha, fullyB, aIndex, reflectionPhase });
        return;
      }

      if (!fullyB && imgA) {
        drawCover(imgA);
      }

      if (fullyB) {
        if (imgB) {
          this.ctx.globalAlpha = 1;
          this.ctx.globalCompositeOperation = 'source-over';
          drawCover(imgB);
        }
      } else if (blendAlpha > 0 && imgB) {
        this.ctx.globalAlpha = blendAlpha;
        this.ctx.globalCompositeOperation = this.blendMode;
        drawCover(imgB);
      }

      this.drawCutReflection({ imgB, phase: reflectionPhase });

      this.ctx.globalAlpha = 1;
      this.ctx.globalCompositeOperation = 'source-over';
    }

    updateCaptions(progress) {
      for (const caption of this.captions) {
        const fadeDuration = clamp(caption.fade, 0.01, 1);
        const fadeIn = caption.start <= 0 ? 1 : smoothstep(caption.start, caption.start + fadeDuration, progress);
        /* 終了位置が100%なら、最後まで出したままにする(開始0%でフェードインしないのと対) */
        const fadeOut =
          caption.end >= 1 ? 1 : 1 - smoothstep(caption.end - fadeDuration, caption.end, progress);
        const opacity = Math.min(fadeIn, fadeOut);
        caption.el.style.opacity = opacity.toFixed(3);
        caption.el.style.visibility = opacity < 0.01 ? 'hidden' : 'visible';
        if (!this.reduced) {
          const isProductShowcase = caption.el.classList.contains('scroll-story__product-showcase');
          /* 商品カードは上下へ動かさず、中心からふんわり大きくなる。 */
          const fadeOffset = isProductShowcase ? 0 : (1 - opacity) * 24;
          /* そのキャプションの表示区間内での進み具合(0→1)。progressによる移動量に使う */
          const span = caption.end - caption.start;
          const local = span > 0 ? clamp((progress - caption.start) / span, 0, 1) : 1;
          const eased = local * local * (3 - 2 * local); // smoothstep で滑らかに
          /* travel は開始位置のずれ、offset は着地点そのもののずれ */
        const travelOffset = caption.travel * (1 - eased) + caption.offset; // vh。開始時 travel → 終了時 0
          /*
          退場時の拡大。fadeOut が 1→0 になるにつれて拡大するので、
          カメラが前進して文字を通り過ぎる（吸い込まれる）ように見える。
        */
        /*
          縮小は表示区間全体(=ズームの進行)に連動させる。
          フェードアウト区間だけで縮めると、最後に急に吸い込まれて見える。
        */
        const exitScale = 1 + (caption.exitZoom - 1) * eased;
        const productEnterScale = isProductShowcase ? 0.78 + 0.22 * fadeIn : 1;
        caption.el.style.transform =
          `translate3d(${caption.offsetX.toFixed(2)}vw, calc(${travelOffset.toFixed(2)}vh + ${fadeOffset.toFixed(2)}px), 0) scale(${(exitScale * productEnterScale).toFixed(4)})`;
        }
      }
    }
  }

  customElements.define('scroll-story', ScrollStory);
})();
